import React from 'react';
import { Helmet } from 'react-helmet-async';

type HeadBlockProps = {
  title?: string;
  ogTitle?: string;
  description?: string;
  ogDescription?: string;
  img?: string;
  canonical?: string;
  firstScript?: string;
  secondScript?: string;
  noindex?: boolean;
};

export const HeadBlock = ({
  title = initialValue.title,
  ogTitle = initialValue.ogTitle,
  description = initialValue.description,
  ogDescription = initialValue.ogDescription,
  img = initialValue.img,
  canonical = initialValue.canonical,
  firstScript = initialValue.firstScript,
  secondScript = initialValue.secondScript,
  noindex = initialValue.noindex,
}: HeadBlockProps): React.ReactElement => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta name="description" content={description}></meta>
      <meta property="og:image" content={img} />
      <link rel="canonical" href={canonical} />
      <script type="application/ld+json">{firstScript}</script>
      <script type="application/ld+json">{secondScript}</script>
      {noindex && <meta name="robots" content="noindex" />}
    </Helmet>
  );
};

const initialValue = {
  title:
    'みんかぶアセットプランナー｜投資管理アプリ 株式投資の資産推移をグラフ化',
  ogTitle:
    'みんかぶアセットプランナー｜投資管理アプリ 株式投資の資産推移をグラフ化',
  description:
    'みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。',
  ogDescription:
    'みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。',
  img: 'https://tw.minkabu.jp/static/images/lp/walk-img-4.png',
  canonical: 'https://tw.minkabu.jp/',
  firstScript: `{
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": "アセプラ(みんかぶアセットプランナー)",
    "image": [
      "https://tw.minkabu.jp/static/images/lp/mainimage.jpg",
      "https://tw.minkabu.jp/static/images/lp/home_shisan.png",
      "https://tw.minkabu.jp/static/images/lp/aruaru_rikaku.png",
      "https://tw.minkabu.jp/static/images/lp/oshirase.png",
      "https://tw.minkabu.jp/mg/lp/aruaru_songiri.png"
     ],
    "description": "みんかぶアセットプランナーは、株式・投資信託を管理、分析する投資管理アプリです。株式の取引履歴や資産推移をグラフ化し、損益をLINEで毎日お知らせ。個人投資家の資産形成をサポートします。",
    "brand": {
      "@type": "Brand",
      "name": "みんかぶ"
    },
  }`,
  secondScript: `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "アセプラ（みんかぶアセットプランナー）とは何ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "資産管理のためのツールです。複数の証券口座に散らばった資産情報をまとめて、様々な分析チャートとともに確認できます。"
        }
      },
      {
        "@type": "Question",
        "name": "登録できる証券会社はどこですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SBI証券 、楽天証券、松井証券、三菱UFJ eスマート証券の口座をご登録いただけます。"
        }
      },
      {
        "@type": "Question",
        "name": "連携が可能な金融商品はなんですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "現在は国内株式（現物・信用）のみ連携が可能です。今後、他の金融商品の連携ができるよう準備を進めてまいります。"
        }
      },
      {
        "@type": "Question",
        "name": "アセプラの始め方は？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "まず、みんかぶ会員登録を行います。登録完了後、ログイン後の「口座一覧」画面からお持ちの証券口座の情報を入力して口座を連携してください。なお、モニター期間終了後にはみんかぶプレミアム（月額1,320円 税込）の登録が必要となります。"
        }
      },
      {
        "@type": "Question",
        "name": "アセプラの利用料金は？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "アセプラのすべての機能のご利用には月額課金サービス「みんかぶプレミアム（税込1,320円/月）」にご登録が必要です。初回登録のみ30日間無料でご利用いただけます。"
        }
      },
      {
        "@type": "Question",
        "name": "口座を登録しましたが、データが表示されません。",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "口座登録直後、データを取得・反映しておりますのでしばらくお待ちください。初回のデータ取得にはお時間がかかる可能性がございますのでご了承ください。<br>2回目以降のデータ更新は毎日17:30ごろから行います。証券会社や取引データの量によってお時間がかかる場合がございますので、しばらくお待ちいただけますようお願いいたします。"
        }
      },
      {
        "@type": "Question",
        "name": "以前、無料体験をしたことがありますが、また無料体験を利用できますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "「無料体験」は、お一人様につき初回登録時の1回限りとなります。"
        }
      },
      {
        "@type": "Question",
        "name": "「無料体験」の期間中でも解約できますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "「無料体験」の期間中に利用停止手続きをすると、無料のまま解約でき、ご利用料金は発生しません。解約ご希望の場合は、必ずご自身でお手続きください。"
        }
      },
      {
        "@type": "Question",
        "name": "料金の請求はいつからですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "「無料体験」の期間中に利用停止手続きをすると、無料のまま解約でき、ご利用料金は発生しません。解約ご希望の場合は、必ずご自身でお手続きください。"
        }
      },
      {
        "@type": "Question",
        "name": "利用できる支払い方法は？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "みんかぶプレミアムのお支払いには、クレジットカードまたはデビットカードをご利用いただけます。<br>※銀行振込、コンビニ払い等は扱っておりません。"
        }
      },
      {
        "@type": "Question",
        "name": "法人契約で利用することは可能ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "「みんかぶプレミアム」で提供するサービスは、個人の私的利用に限定させていただいております。 法人契約でのご利用はできません。"
        }
      },
      {
        "@type": "Question",
        "name": "みんかぶプレミアムを解約後、また利用を再開することはできますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "はい、<a href='https://id.minkabu.jp/settings'>みんかぶ会員画面</a>より再開することが可能です。「解約」は定期購入の停止という扱いとなり、一時停止中の取引履歴は取得されません。"
        }
      },
      {
        "@type": "Question",
        "name": "みんかぶプレミアムの解約手続き後、いつまで利用することができますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "解約は定期購入の停止という扱いとなります。定期購入の停止のお手続き後、次回契約更新日の前日までご利用いただけます。"
        }
      }
    ]
  }`,
  noindex: false,
};
